/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  QueryList,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AppRouter } from '@gh/common-shared';
import { isNotNil, parseUrl } from '@gh/core-util';
import { distinctUntilChanged$, filter$, map$ } from '@gh/rx/operators';
import { find, isString, keyBy } from 'lodash';
import { Subscription } from 'rxjs';
import { NavLink } from './nav-link';
import { NavTabComponent } from './nav-tab.component';

export class NavTabChangeEvent {
  name: string;
}

/**
 * gh-nav-tab-bar component allows to organize tab-based navigation on the page.
 * This component allows to describe available tabs and displayed content
 * separately on different component level or even in different components.
 *
 * It is used primarily for routing based navigation.
 * Although it allows to turn off support of native angular routing.
 */
@Component({
  selector: 'gh-nav-tab-bar',
  template: require('./nav-tab-bar.component.html'),
  styles: [require('./nav-tab-bar.component.scss')],
  exportAs: 'ghNavTabBar',
})
export class NavTabBarComponent implements AfterContentInit, OnDestroy {
  /**
   * Whether location should be modified when user navigates between tabs
   */
  @Input() saveLocation: boolean = false;

  /**
   * In one way mode developer always has to set currently selected tab.
   * Clicking on tab of navigation component does not select this tab automatically.
   * Such behavior is necessary for pages based on immutable store to allow some pre-processing
   * before tab has selected.
   */
  @Input() oneWayMode: boolean = false;

  /**
   * Whether navigation is based on native Angular routing.
   * If this property is set to false, developer has to set take over routing by himself.
   */
  @Input() routing: boolean = true;

  /**
   * Triggered only in oneWayMode when changing of tab was requested.
   * Subscriber does not have to change selected tab.
   */
  @Output() request = new EventEmitter<NavTabChangeEvent>();

  /**
   * Triggered when current tab has changed
   */
  // tslint:disable-next-line:no-output-named-after-standard-event
  @Output() change = new EventEmitter<NavTabChangeEvent>();

  links: NavLink[];
  activeLink?: NavLink;

  // tslint:disable-next-line:no-forward-ref
  @ContentChildren(forwardRef(() => NavTabComponent)) tabs: QueryList<NavTabComponent>;

  private linkIndex: Hash<NavLink>;

  private initTabName: string;
  private initialized: boolean = false;
  private routerSubscription: Subscription;
  private wasFirstNavigation = false;

  constructor(private router: Router,
              private appRouter: AppRouter) {

  }

  /**
   * Sets currently selected tab by name
   */
  @Input()
  set current(current: Maybe<string>) {
    if (!current) {
      return;
    }

    if (this.initialized) {
      this.navigateTo(current);
    } else {
      this.initTabName = current;
    }
  }

  /**
   * Returns currently selected tab
   */
  get current(): Maybe<string> {
    const { activeLink } = this;
    return activeLink && activeLink.name;
  }

  ngAfterContentInit(): void {
    this.routerSubscription = this.router.events.pipe(
      filter$((event) => event instanceof NavigationEnd),
      // track only pathname changes (changes in query params do not affect nav-tab-bar)
      map$((event: NavigationEnd) => parseUrl(event.url).pathname),
      distinctUntilChanged$())
      .subscribe(() => this.updateActiveLink());

    this.tabs.changes.subscribe(() => this.updateActiveLink());

    this.updateActiveLink();

    this.initialized = true;
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Navigates to the provided tab (by tab name or using NavLink object)
   */
  navigateTo(next: NavLink | string): void {
    const nextLink = isString(next) ? this.linkIndex[next] : next;

    if (nextLink === this.activeLink) {
      return;
    }

    if (!this.routing) {
      this.setActiveLink(nextLink);
    } else if (nextLink.url) {
      // there is no sense to navigate by URL when this URL is already active
      if (this.appRouter.isActive(nextLink.url, false)) {
        this.setActiveLink(nextLink);
      } else {
        this.appRouter.navigateByUrl(nextLink.url, {
            replaceUrl: !this.wasFirstNavigation,
            skipLocationChange: this.saveLocation,
          })
          .then((success) => {
            if (success) {
              this.wasFirstNavigation = true;
              this.setActiveLink(nextLink);
            }
          });
      }
    }
  }

  /**
   * Selects one of available tabs
   */
  updateActiveLink(): void {
    this.links = <NavLink[]>this.tabs.map((tab) => tab.getNavLink()).filter((link) => link && link.visible);
    this.linkIndex = keyBy(this.links, 'name');

    // try to use initially requested tab name
    let nextLink = !this.initialized && this.initTabName ? this.linkIndex[this.initTabName] : void 0;

    // try to find link for the current route
    if (!nextLink && this.routing) {
      nextLink = find(this.links, (link) => link.url ? this.appRouter.isActive(link.url, false) : false);
    }

    // try to find default route
    if (!nextLink) {
      nextLink = find(this.links, (link) => link.useByDefault);
    }

    // otherwise take the first link
    if (!nextLink) {
      nextLink = find<NavLink>(this.links, (link) => isNotNil(link.url));
    }

    if (nextLink) {
      this.navigateTo(nextLink);
    }
  }

  onClick(link: NavLink): void {
    if (this.oneWayMode) {
      this.request.emit({ name: link.name });
    } else {
      this.navigateTo(link);
    }
  }

  private setActiveLink(link: NavLink): void {
    this.activeLink = link;
    this.change.emit({ name: this.activeLink.name });
  }
}
